
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface StripeCheckoutParams {
  planId: string;
  organizationId: string;
  isYearly: boolean;
  returnUrl: string;
}

export async function initiateStripeCheckout({
  planId,
  organizationId,
  isYearly,
  returnUrl
}: StripeCheckoutParams): Promise<{ url?: string; error?: string }> {
  try {
    console.log("Initialisation du paiement Stripe pour:", { planId, organizationId, isYearly, returnUrl });
    
    // Utiliser l'URL correcte pour l'environnement
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://eledlxtrcasnxjjarfti.supabase.co";
    console.log("Utilisation de l'URL Supabase:", supabaseUrl);
    
    // Construire l'URL en fonction de l'environnement
    const paymentUrl = `${supabaseUrl}/functions/v1/create-checkout-session`;
    console.log("URL de l'API de paiement:", paymentUrl);
    
    if (!navigator.onLine) {
      const errorMessage = "Vous semblez être hors ligne. Veuillez vérifier votre connexion internet.";
      toast.error(errorMessage);
      return { error: errorMessage };
    }

    // Récupérer la session Supabase et le token
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData?.session) {
      console.error("Erreur de session:", sessionError);
      
      return { error: "Session invalide ou expirée. Veuillez rafraîchir la page." };
    }

    const jwt = sessionData.session.access_token;
    
    if (!jwt) {
      return { error: "Token d'authentification non trouvé. Veuillez vous reconnecter." };
    }

    return await processStripeCheckout(paymentUrl, jwt, planId, organizationId, isYearly, returnUrl);
    
  } catch (error: any) {
    console.error('Erreur lors de l\'initialisation du paiement Stripe:', error);
    
    let errorMessage = error.message;
    if (error.message === "Failed to fetch") {
      errorMessage = "Impossible de communiquer avec le serveur. Cela peut être dû à une restriction CORS ou à un problème de réseau.";
    }
    
    return { error: errorMessage };
  }
}

async function processStripeCheckout(
  paymentUrl: string, 
  jwt: string, 
  planId: string, 
  organizationId: string, 
  isYearly: boolean, 
  returnUrl: string
): Promise<{ url?: string; error?: string }> {
  // Amélioration : ajout d'un timeout et gestion spécifique pour CORS
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout

  try {
    console.log("Envoi de la requête à:", paymentUrl);
    console.log("Avec le token JWT:", jwt.substring(0, 10) + "...");
    
    const response = await fetch(paymentUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
        'Authorization': `Bearer ${jwt}`,
      },
      mode: 'cors',
      body: JSON.stringify({
        planId,
        organizationId,
        isYearly,
        returnUrl: returnUrl || window.location.origin,
      }),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    console.log("Statut de la réponse:", response.status);

    if (!response.ok) {
      let errorMessage = "Erreur lors de la communication avec l'API de paiement";
      
      switch (response.status) {
        case 404:
          return { error: "La fonction de paiement n'est pas déployée. Essayez quand même." };
        case 401:
          return { error: "Session expirée. Veuillez rafraîchir la session." };
        case 403:
          return { error: "Accès non autorisé. Veuillez vérifier vos permissions." };
        case 500:
          return { error: "Erreur serveur. Notre équipe a été notifiée." };
        case 503:
          return { error: "Le service est temporairement indisponible. Veuillez réessayer." };
      }
      
      console.error(`Erreur HTTP ${response.status}:`, errorMessage);
      return { error: errorMessage };
    }

    const responseText = await response.text();
    console.log("Réponse brute de l'API de paiement:", responseText);

    if (!responseText || !responseText.trim()) {
      return { error: "Réponse vide du serveur" };
    }

    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error("Erreur lors du parsing JSON:", parseError);
      return { error: "Format de réponse invalide" };
    }

    if (data.error) {
      return { error: data.error };
    }

    if (!data.url) {
      return { error: "URL de paiement non reçue" };
    }

    return { url: data.url };
    
  } catch (fetchError: any) {
    clearTimeout(timeoutId);
    
    if (fetchError.name === 'AbortError') {
      return { error: "La requête a pris trop de temps. Veuillez réessayer plus tard." };
    }
    
    console.error("Erreur fetch lors du checkout:", fetchError);
    return { error: fetchError.message || "Erreur réseau lors de la communication avec Stripe" };
  }
}
